/**
 * Publish Schema Inspector
 *
 * Pure helpers that analyse a flow's nodes/edges and produce:
 *   1. The input/output schema that the publish view will see
 *      (mirrors server/services/FlowExecutionService.ts getInputSchema/getOutputSchema)
 *   2. A list of "auto-add" suggestions: which source generators have hardcoded
 *      prompts that could become user inputs, and which sink generators don't
 *      have apiOutput flags.
 *
 * Used by PublishModal to validate before publishing, and by the
 * autoAddPublishIO mutator below to actually patch the live editor state.
 */
import type { Node, Edge } from '@xyflow/react';
import { NODE_REGISTRY } from '../config/nodeRegistry';

export interface InspectInputEntry {
  nodeId: string;
  label: string;
  type: 'text' | 'image' | 'video' | 'audio';
  required: boolean;
}

export interface InspectOutputEntry {
  nodeId: string;
  label: string;
  type: string;
}

export interface AutoAddSuggestion {
  /** Source generators with hardcoded prompts that could be turned into inputs */
  sourcesNeedingInput: Array<{
    nodeId: string;
    nodeType: string;
    /** What kind of input panel we'd add */
    panelType: 'documentPanel' | 'imagePanel';
    /** The current prompt text we'd clear (for preview) */
    currentPrompt: string;
  }>;
  /** Sink generators that don't yet have apiOutput=true */
  sinksNeedingOutput: Array<{
    nodeId: string;
    nodeType: string;
    /** Suggested label */
    suggestedLabel: string;
  }>;
}

export interface InspectionResult {
  inputs: InspectInputEntry[];
  outputs: InspectOutputEntry[];
  suggestions: AutoAddSuggestion;
  /** Convenience flags */
  hasInputs: boolean;
  hasOutputs: boolean;
  /** Total fixable issues */
  fixableCount: number;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/**
 * Walk the registry once to build a quick lookup by camelCase type.
 * The registry is keyed by PascalCase, but React Flow node `type` is camelCase.
 */
const REGISTRY_BY_CAMEL: Record<string, (typeof NODE_REGISTRY)[keyof typeof NODE_REGISTRY]> = {};
for (const reg of Object.values(NODE_REGISTRY)) {
  REGISTRY_BY_CAMEL[reg.camelCaseType] = reg;
}

function getReg(camelType: string) {
  return REGISTRY_BY_CAMEL[camelType];
}

function isInputContainer(camelType: string): boolean {
  return camelType === 'imagePanel' || camelType === 'documentPanel';
}

function isGenerator(camelType: string): boolean {
  const reg = getReg(camelType);
  return !!reg && reg.handlerType !== 'none';
}

function getOutputsMediaType(camelType: string): string | undefined {
  const reg = getReg(camelType);
  return reg?.definition?.outputsMediaType as string | undefined;
}

function findSourceIds(nodes: Node[], edges: Edge[]): Set<string> {
  const targets = new Set(edges.map((e) => e.target));
  return new Set(nodes.filter((n) => !targets.has(n.id)).map((n) => n.id));
}

function findSinkIds(nodes: Node[], edges: Edge[]): Set<string> {
  const sources = new Set(edges.map((e) => e.source));
  return new Set(nodes.filter((n) => !sources.has(n.id)).map((n) => n.id));
}

/**
 * Pull the prompt-ish content out of a node's data. Generators usually use
 * `prompt`; some use `value` or `content`.
 */
function getPromptText(node: Node): string {
  const d: any = node.data || {};
  return (d.prompt || d.value || d.content || '').toString().trim();
}

/**
 * Decide whether a generator's primary input is image-shaped (e.g. WanAnimate,
 * KlingAvatar) vs text-shaped. Text-shaped is the default since most flows
 * start from a text prompt.
 */
function panelTypeForGenerator(camelType: string): 'documentPanel' | 'imagePanel' {
  const reg = getReg(camelType);
  if (!reg) return 'documentPanel';
  const accepts: string[] = (reg.definition?.acceptsMediaTypes as string[] | undefined) || [];
  // If it accepts text, prefer text. Otherwise fall back to image.
  if (accepts.includes('text')) return 'documentPanel';
  if (accepts.includes('image')) return 'imagePanel';
  return 'documentPanel';
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Mirrors server/services/FlowExecutionService.ts getInputSchema, but operates
 * on React Flow nodes/edges instead of saved DB state.
 */
export function getClientInputSchema(nodes: Node[], edges: Edge[]): InspectInputEntry[] {
  // Prefer explicitly-flagged inputs (mirror of server getInputSchema).
  // Flagged inputs are ALWAYS required — pre-existing content is a local
  // placeholder, not a default. The publish runner ignores it.
  const apiInputNodes = nodes.filter((n) => !!(n.data as any)?.apiInput);
  if (apiInputNodes.length > 0) {
    return apiInputNodes.map((node) => {
      const camelType = node.type as string;
      const reg = getReg(camelType);
      const outputType = getOutputsMediaType(camelType);
      const mediaType: InspectInputEntry['type'] =
        outputType === 'image' ? 'image' : outputType === 'video' ? 'video' : 'text';
      const d: any = node.data || {};
      return {
        nodeId: node.id,
        label: (d.apiInputLabel as string) || d.label || d.name || reg?.type || node.id,
        type: mediaType,
        required: true,
      };
    });
  }

  const sourceIds = findSourceIds(nodes, edges);
  const schema: InspectInputEntry[] = [];

  for (const node of nodes) {
    if (!sourceIds.has(node.id)) continue;
    const camelType = node.type as string;
    const reg = getReg(camelType);
    if (!reg) continue;

    if (isInputContainer(camelType)) {
      const outputType = getOutputsMediaType(camelType);
      const mediaType = outputType === 'image' ? 'image' : outputType === 'video' ? 'video' : 'text';
      const d: any = node.data || {};
      const hasExisting = !!(d.content || d.value || d.imageUrl || '').toString().trim();
      schema.push({
        nodeId: node.id,
        label: d.label || d.name || reg.type,
        type: mediaType,
        required: !hasExisting,
      });
      continue;
    }

    // Mirrors the dictation branch in server getInputSchema: its content lives
    // in `transcription`, which getPromptText does not read, so the generic
    // branch below marked it required even when a recording existed. Diverging
    // here would make the publish modal disagree with what the server accepts.
    if (camelType === 'dictation') {
      const d: any = node.data || {};
      const hasTranscript = !!(d.transcription || d.content || d.output || '').toString().trim();
      schema.push({
        nodeId: node.id,
        label: d.label || d.name || reg.type,
        type: 'text',
        required: !hasTranscript,
      });
      continue;
    }

    if (isGenerator(camelType)) {
      const hasPrompt = !!getPromptText(node);
      if (!hasPrompt) {
        const d: any = node.data || {};
        schema.push({
          nodeId: node.id,
          label: d.label || d.name || reg.type,
          type: 'text',
          required: true,
        });
      }
    }
  }

  return schema;
}

/**
 * Mirrors server getOutputSchema. Prefers nodes flagged with apiOutput; falls
 * back to all sinks.
 */
export function getClientOutputSchema(nodes: Node[], edges: Edge[]): InspectOutputEntry[] {
  const apiOutputNodes = nodes.filter((n) => !!(n.data as any)?.apiOutput);
  if (apiOutputNodes.length > 0) {
    return apiOutputNodes.map((node) => {
      const d: any = node.data || {};
      return {
        nodeId: node.id,
        label: (d.apiOutputLabel as string) || node.id,
        type: d.type || getOutputsMediaType(node.type as string) || 'any',
      };
    });
  }

  const sinkIds = findSinkIds(nodes, edges);
  const schema: InspectOutputEntry[] = [];
  for (const node of nodes) {
    if (!sinkIds.has(node.id)) continue;
    const camelType = node.type as string;
    const reg = getReg(camelType);
    if (!reg) continue;
    const d: any = node.data || {};
    schema.push({
      nodeId: node.id,
      label: d.label || d.name || reg.type,
      type: getOutputsMediaType(camelType) || d.type || 'any',
    });
  }
  return schema;
}

/**
 * Inspect a flow and produce schema + auto-add suggestions in one pass.
 */
export function inspectFlowForPublish(nodes: Node[], edges: Edge[]): InspectionResult {
  const inputs = getClientInputSchema(nodes, edges);
  const outputs = getClientOutputSchema(nodes, edges);

  const sourceIds = findSourceIds(nodes, edges);
  const sinkIds = findSinkIds(nodes, edges);

  const sourcesNeedingInput: AutoAddSuggestion['sourcesNeedingInput'] = [];
  for (const node of nodes) {
    if (!sourceIds.has(node.id)) continue;
    const camelType = node.type as string;
    if (!isGenerator(camelType)) continue;
    const promptText = getPromptText(node);
    if (!promptText) continue; // already a publish input (covered by getClientInputSchema)
    sourcesNeedingInput.push({
      nodeId: node.id,
      nodeType: camelType,
      panelType: panelTypeForGenerator(camelType),
      currentPrompt: promptText,
    });
  }

  const sinksNeedingOutput: AutoAddSuggestion['sinksNeedingOutput'] = [];
  // Only suggest output flagging if there are sinks AND none already explicitly flagged.
  // (When at least one node has apiOutput=true the schema only counts those.)
  const hasAnyApiOutput = nodes.some((n) => !!(n.data as any)?.apiOutput);
  if (!hasAnyApiOutput) {
    let outIndex = 1;
    for (const node of nodes) {
      if (!sinkIds.has(node.id)) continue;
      const camelType = node.type as string;
      const reg = getReg(camelType);
      if (!reg) continue;
      // Only generator sinks (skip Output containers — they're already display-only)
      if (!isGenerator(camelType)) continue;
      const outType = getOutputsMediaType(camelType) || 'output';
      sinksNeedingOutput.push({
        nodeId: node.id,
        nodeType: camelType,
        suggestedLabel: `${outType}_${outIndex++}`,
      });
    }
  }

  const fixableCount = sourcesNeedingInput.length + sinksNeedingOutput.length;

  return {
    inputs,
    outputs,
    suggestions: {
      sourcesNeedingInput,
      sinksNeedingOutput,
    },
    hasInputs: inputs.length > 0,
    hasOutputs: outputs.length > 0,
    fixableCount,
  };
}

// ---------------------------------------------------------------------------
// Auto-add mutator
// ---------------------------------------------------------------------------

/**
 * Compute the (nodes, edges) patches needed to apply the auto-add suggestions.
 *
 * Returns:
 *   - `nodesToAdd`:    new ImagePanel/DocumentPanel nodes to insert
 *   - `edgesToAdd`:    edges connecting each new panel to its target generator
 *   - `nodeUpdates`:   per-node-id partial-data updates (clear prompts, set apiOutput)
 *
 * The caller is responsible for actually applying these via setNodes/setEdges
 * (CreativeNodeFlow has the live React Flow handles).
 *
 * Why this lives client-side: react-flow state is the source of truth for
 * unsaved edits, so we can't do this on the server.
 */
export interface AutoAddPatch {
  nodesToAdd: Node[];
  edgesToAdd: Edge[];
  nodeUpdates: Array<{
    nodeId: string;
    data: Record<string, unknown>;
  }>;
  /** Human-readable summary for the toast */
  summary: string;
}

export function buildAutoAddPatch(
  nodes: Node[],
  inspection: InspectionResult,
): AutoAddPatch {
  const nodesToAdd: Node[] = [];
  const edgesToAdd: Edge[] = [];
  const nodeUpdates: AutoAddPatch['nodeUpdates'] = [];

  const PANEL_OFFSET_X = 520; // place new panel to the LEFT of the generator
  const PANEL_OFFSET_Y = 0;
  const PANEL_WIDTH = 420;
  const PANEL_HEIGHT = 300;

  // 1. Add input panels for source generators with hardcoded prompts
  for (const sugg of inspection.suggestions.sourcesNeedingInput) {
    const target = nodes.find((n) => n.id === sugg.nodeId);
    if (!target) continue;

    const panelId = `${sugg.panelType}-publish-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const panelType = sugg.panelType;
    const isImage = panelType === 'imagePanel';

    const panelNode: Node = {
      id: panelId,
      type: panelType,
      position: {
        x: target.position.x - PANEL_OFFSET_X,
        y: target.position.y + PANEL_OFFSET_Y,
      },
      width: PANEL_WIDTH,
      height: PANEL_HEIGHT,
      data: isImage
        ? { imageUrl: undefined, imageFile: undefined }
        : { content: '', pages: [], currentPageIndex: 0 },
    };
    nodesToAdd.push(panelNode);

    edgesToAdd.push({
      id: `epub-${panelId}-${target.id}`,
      source: panelId,
      target: target.id,
      type: isImage ? 'image' : 'text',
      animated: false,
      style: {
        stroke: isImage ? 'var(--color-type-image)' : 'var(--color-type-text)',
        strokeWidth: 3,
      },
    });

    // Clear the hardcoded prompt so the schema will see this generator as
    // "fed by an input" rather than "has a hardcoded prompt"
    nodeUpdates.push({
      nodeId: target.id,
      data: { prompt: '' },
    });
  }

  // 2. Flag sink generators with apiOutput
  for (const sugg of inspection.suggestions.sinksNeedingOutput) {
    nodeUpdates.push({
      nodeId: sugg.nodeId,
      data: {
        apiOutput: true,
        apiOutputLabel: sugg.suggestedLabel,
      },
    });
  }

  // Build summary
  const parts: string[] = [];
  if (inspection.suggestions.sourcesNeedingInput.length > 0) {
    const n = inspection.suggestions.sourcesNeedingInput.length;
    parts.push(`${n} input${n === 1 ? '' : 's'}`);
  }
  if (inspection.suggestions.sinksNeedingOutput.length > 0) {
    const n = inspection.suggestions.sinksNeedingOutput.length;
    parts.push(`${n} output${n === 1 ? '' : 's'}`);
  }
  const summary = parts.length > 0 ? `Added ${parts.join(' and ')}` : 'Nothing to add';

  return { nodesToAdd, edgesToAdd, nodeUpdates, summary };
}
